<template>
  <div class="gs-asset-type">
    <div class="gs-asset-split">
      <template v-for="(review, index) in videoReviews">
        <section
          v-if="isVideoReviewTabActive(review)"
          :key="review.id"
          class="gs-asset-editor"
        >
          <header class="gs-asset-editor__head">
            <div class="gs-asset-editor__head-fields">
              <gs-field :label="$t('frontend_resources_name')">
                <text-input
                  v-model="review.name"
                  :validator="$v.localFunnelResource.video_reviews.$each[index].name"
                  :attribute="$t('frontend_groovesell_video_review_name')"
                  :placeholder="$t('frontend_resources_name')"
                />
              </gs-field>
              <gs-field :label="$t('frontend_resources_link_to')">
                <gs-name-select
                  v-model="review.landingPage"
                  size="full"
                  max-height="220px"
                  :options="landingPagesOptions"
                />
              </gs-field>
            </div>
            <div class="gs-asset-editor__controls">
              <span class="gs-asset-editor__toggle">
                <span>Show to affiliates</span>
                <gd-toggle v-model="review.enabled" :value="review.enabled" />
              </span>
              <button
                type="button"
                class="gs-asset-editor__delete"
                data-action="delete-video-review"
                title="Delete video review"
                aria-label="Delete video review"
                @click="openConfirmModal(index)"
              >
                <trash-icon class="w-4 h-4 fill-current" />
              </button>
            </div>
          </header>

          <div class="gs-asset-editor__section">
            <field-error
              :validator="$v.localFunnelResource.video_reviews.$each[index].description"
              :attribute="$t('frontend_desc')"
            >
              <template #default="{ hasErrors, message }">
                <gs-field :label="$t('frontend_resources_description')" :error="message">
                  <textarea
                    v-model="review.description"
                    class="gs-textarea gs-textarea--short"
                    :class="{ 'has-error': hasErrors }"
                    :placeholder="$t('frontend_resources_description')"
                  ></textarea>
                </gs-field>
              </template>
            </field-error>
            <div class="gs-asset-editor__foot">
              <span class="gs-asset-editor__count">{{ characterCount(review.description) }}</span>
              <gs-name-select placeholder="Insert" max-height="min(280px, 40vh)" :actions="descriptionInsertActions(index)" size="action" />
            </div>
          </div>

          <div class="gs-asset-editor__section">
            <gs-field :label="$t('frontend_resources_video')">
              <text-input
                v-model="review.video"
                :validator="$v.localFunnelResource.video_reviews.$each[index].video"
                :attribute="$t('frontend_resources_video')"
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </gs-field>
            <div class="gs-video-reviews__preview">
              <video-preview :url="review.video" />
            </div>
          </div>
        </section>
      </template>

      <!-- Only shown when there is something to pick: with an empty list the
           list's own empty state is the single message. -->
      <div v-if="hasVideoReviews && !selectedVideoReview" class="gs-asset-empty">
        <p>Select a video review from the list to edit it.</p>
      </div>
      <asset-item-list
        title="Video reviews"
        :items="videoReviews"
        :selected-id="selectedVideoReviewId"
        :add-label="$t('frontend_resources_add_new')"
        name-fallback="Untitled video review"
        :empty-copy="$t('frontend_resources_ou_currently_donot_have_any_video')"
        enabled-key="enabled"
        :error-ids="videoReviewsErrorIds"
        @select="selectVideoReview"
        @add="createVideoReview()"
        @reorder="reorderVideoReviews"
      />
    </div>

    <gs-alert-dialog
      v-model="confirmOpen"
      title="Delete this video review?"
      :copy="$t('frontend_non_revertable')"
      :confirm-label="$t('frontend_delete_confirm')"
      @confirm="deleteVideo()"
    />
  </div>
</template>

<script>
import urlParser from 'js-video-url-parser/lib/base';
import 'js-video-url-parser/lib/provider/youtube';
import 'js-video-url-parser/lib/provider/vimeo';
import generateNumberMixin from '@/mixins/generateNumberMixin';
import { required, minLength, maxLength, url } from 'vuelidate/lib/validators';
import TrashIcon from '@/assets/images/icons/TrashIcon.svg';
import AssetItemList from './AssetItemList.vue';
import FieldError from './FieldError.vue';
import VideoPreview from './VideoPreview.vue';
import { toTabOrder, sortByTabOrder, idFromTabValue } from './tabOrder';

export default {
  components: {
    AssetItemList,
    FieldError,
    VideoPreview,
    TrashIcon,
  },
  mixins: [generateNumberMixin],
  props: {
    funnelResource: {
      type: Object,
      required: true,
    },
    landingPages: {
      type: Array,
      required: true,
    },
    videoReviewsTab: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      videoReviewId: null,
      confirmOpen: false,
      localFunnelResource: this.funnelResource,
      localVideoReviewsTab: this.videoReviewsTab,
    };
  },
  computed: {
    landingPagesOptions() {
      return this.landingPages.map((landingPage) => {
        return {
          value: landingPage.id,
          text: landingPage.description,
        };
      });
    },
    videoReviews() {
      return (this.localFunnelResource && this.localFunnelResource.video_reviews) || [];
    },
    hasVideoReviews() {
      return (
        this.localFunnelResource &&
        this.localFunnelResource.video_reviews &&
        this.localFunnelResource.video_reviews.length
      );
    },
    selectedVideoReview() {
      return this.videoReviews.filter((review) => this.isVideoReviewTabActive(review))[0] || null;
    },
    selectedVideoReviewId() {
      if (!this.localVideoReviewsTab) return null;
      return idFromTabValue('video_review', this.localVideoReviewsTab);
    },
    videoReviewsErrorIds() {
      let videoReviewsErrorIds = [];

      let index;
      for (index in this.$v.localFunnelResource.video_reviews.$each.$iter) {
        if (this.$v.localFunnelResource.video_reviews.$each.$iter[index].$error) {
          videoReviewsErrorIds.push(
            this.$v.localFunnelResource.video_reviews.$each.$iter[index].$model.id
          );
        }
      }

      return videoReviewsErrorIds;
    },
  },
  mounted() {
    this.$root.$on('trigger-validation', () => {
      this.$v.$touch();
    });
  },
  methods: {
    characterCount(value) {
      const length = (value || '').length;
      return `${length} ${length === 1 ? 'character' : 'characters'}`;
    },
    affiliateLinksActions(index) {
      let affiliateLinksActions = [];

      if (this.landingPages.length > 0) {
        for (let i = 0; i < this.landingPages.length; i++) {
          affiliateLinksActions.push({
            text: this.landingPages[i].description,
            callback: () => this.addAffiliateLinkToVideoReview(index, this.landingPages[i].id),
          });
        }
      }

      return affiliateLinksActions;
    },
    descriptionInsertActions(index) {
      return [
        {
          text: this.$t('frontend_resources_add_affiliate_name'),
          callback: () => this.addAffiliateNameToVideoReview(index),
        },
        ...this.affiliateLinksActions(index),
      ];
    },
    updateFunnelResource() {
      this.$emit('updateFunnelResource', this.localFunnelResource);
    },
    selectVideoReview(id) {
      this.handleVideoReviewsTabChange(`video_review_tab_${id}`);
    },
    // `AssetItemList` reports the new order as ids; the existing handler speaks
    // the tab shape gd-tabs used, so the ids are wrapped back into it.
    reorderVideoReviews(ids) {
      this.handleVideoReviewsTabOrderUpdate(toTabOrder('video_review', ids));
    },
    handleVideoReviewsTabChange(newTab) {
      this.localVideoReviewsTab = newTab || this.localVideoReviewsTab;
      this.$emit('handleVideoReviewsTabChange', this.localVideoReviewsTab);
    },
    handleVideoReviewsTabOrderUpdate(updatedTabs) {
      sortByTabOrder(this.localFunnelResource.video_reviews, 'video_review', updatedTabs);

      this.updateFunnelResource();
    },
    createVideoReview() {
      const newPostId = this.generateNumber();
      const newPostCount = this.localFunnelResource.video_reviews.length + 1;
      const newLandingPageId = this.landingPages.length ? this.landingPages[0].id : '';

      this.localFunnelResource.video_reviews.push({
        id: newPostId,
        name: `Video Review ${newPostCount}`,
        description: '',
        video: '',
        landingPage: newLandingPageId,
      });

      this.localVideoReviewsTab = `video_review_tab_${newPostId}`;

      this.updateFunnelResource();
      this.handleVideoReviewsTabChange();
    },
    openConfirmModal(videoReviewId) {
      this.videoReviewId = videoReviewId;
      this.confirmOpen = true;
    },
    async deleteVideo() {
      this.localFunnelResource.video_reviews.splice(this.videoReviewId, 1);
      this.localVideoReviewsTab = '';

      if (this.localFunnelResource.video_reviews.length > 0) {
        let nextOpenIndex = 0;
        if (this.videoReviewId != 0) {
          nextOpenIndex = this.videoReviewId - 1;
        }

        const nextOpenId = this.localFunnelResource.video_reviews[nextOpenIndex].id;
        this.localVideoReviewsTab = `video_review_tab_${nextOpenId}`;
      }
      this.updateFunnelResource();
      this.handleVideoReviewsTabChange();
    },
    addAffiliateNameToVideoReview(index) {
      this.localFunnelResource.video_reviews[
        index
      ].description = `${this.localFunnelResource.video_reviews[index].description}##affiliateName##`;

      this.updateFunnelResource();
    },
    addAffiliateLinkToVideoReview(index, landingPageId) {
      this.localFunnelResource.video_reviews[
        index
      ].description = `${this.localFunnelResource.video_reviews[index].description}##affiliateLink${landingPageId}##`;

      this.updateFunnelResource();
    },
    isVideoReviewTabActive(review) {
      return this.localVideoReviewsTab === `video_review_tab_${review.id}`;
    },
  },
  validations: {
    localFunnelResource: {
      video_reviews: {
        $each: {
          name: {
            required,
            minLength: minLength(3),
            maxLength: maxLength(80),
          },
          description: {
            required,
            minLength: minLength(3),
          },
          video: {
            required,
            url,
            isValidVideoURL(value, object) {
              if (urlParser.parse(value) === undefined) {
                return false;
              }
              return true;
            },
          },
          $trackBy: 'id',
        },
      },
    },
  },
};
</script>

<style src="./asset-split.css"></style>

<style scoped>
.gs-video-reviews__preview {
  margin-top: 12px;
}
</style>
